/**
 * EpilimneticTargetedMixingPage - Targeted Epilimnetic Mixing (TEM)
 *
 * Australian research-derived module for warm-water summer aeration regimes.
 * Designed for shallow polymictic reservoirs (e.g. Barcombe in July-August)
 * where full destratification is over-engineered and would collapse the cold
 * raw-water supply zone.
 *
 * Operating principle: run the existing diffuser array at reduced Q_air so the
 * bubble plume terminates at the surface-mixed-layer (SML) base, disrupting
 * buoyant cyanobacterial vertical migration without mixing the hypolimnion.
 *
 * References:
 *   - Patterson, Hamblin & Imberger 1984 (Limnology & Oceanography 29(4): 845-861)
 *   - Hamilton & Schladow 1997 (Ecological Modelling 96: 91-110)
 *   - Brookes & Carey 2011 (Science 334: 46-47)
 *   - Antenucci, Brookes & Hipsey 2005 (Centre for Water Research, UWA)
 *
 * Demonstrated sites: Chaffey Dam (NSW), Cotter Dam (ACT), Sugarloaf Reservoir
 * (Vic), Lake Burragorang (NSW).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Waves, ArrowLeft, Info, TrendingDown, ShieldCheck } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function EpilimneticTargetedMixingPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('tem_epilimnetic');

  // Inputs
  const [surfTempC, setSurfTempC] = useState(22);          // summer surface
  const [hypoTempC, setHypoTempC] = useState(13);          // summer hypolimnion
  const [meanDepth, setMeanDepth] = useState(4.5);         // m - Barcombe-like
  const [smlDepth,  setSmlDepth]  = useState(1.5);         // m - Surface Mixed Layer depth
  const [windMs,    setWindMs]    = useState(2.5);
  const [Qair_full, setQairFull]  = useState(2.5);         // L/s per diffuser at full mix
  const [diffusers, setDiffusers] = useState(20);
  const [chl_ugL,   setChl]       = useState(35);
  const [fCyano,    setFCyano]    = useState(0.50);

  const R = useMemo(() => {
    // Thermal stratification strength: density difference between surface and hypolimnion
    const rho = (T: number) => 1000 * (1 - 6.63e-6 * Math.pow(Math.max(0, T - 4), 1.68));
    const dRho = Math.abs(rho(hypoTempC) - rho(surfTempC));

    // Surface mixed layer depth target (Hamilton 2003 critical-depth criterion):
    // SML must exceed photic depth so cyano cannot maintain net positive growth.
    // Photic depth ~ 4.6 / k_d; k_d driven by chl-a (background 0.5 + 0.015 chl)
    const kd = 0.5 + 0.015 * chl_ugL;
    const z_eu = 4.6 / Math.max(0.1, kd);
    const z_crit = z_eu * 1.6;  // critical depth ~ 1.6 x euphotic depth

    // TEM target Q_air: only enough to mix to SML base (not full depth)
    // Empirical: Q_TEM / Q_full ~ (z_SML / z_mean)^1.5
    const ratio = Math.pow(smlDepth / Math.max(0.1, meanDepth), 1.5);
    const Qair_TEM = Math.max(0.05, Qair_full * ratio);
    const energy_saving_pc = 100 * (1 - ratio);

    // Cyano suppression index (Brookes-Carey resilience score):
    // higher = better suppression of buoyant Microcystis
    const smlAdequacy = Math.min(2, smlDepth / Math.max(0.5, z_crit));
    const cyanoSuppr = Math.min(100, 100 * smlAdequacy * (1 - fCyano * 0.4));

    // Whether TEM is the appropriate regime (warm, stratified, cyano-prone):
    const regime_ok = surfTempC > 18 && (surfTempC - hypoTempC) > 4 && fCyano > 0.25;

    // OTE re-cast under TEM (shorter plume path -> lower OTE per device but lower
    // total demand because much less air used overall)
    const ote_full = 1 - Math.exp(-meanDepth / 8 * (3 / 3));  // ref McGinnis-style
    const ote_TEM  = 1 - Math.exp(-smlDepth  / 8 * (3 / 3));
    const ote_ratio_pc = (ote_TEM / ote_full) * 100;

    return {
      dRho, kd, z_eu, z_crit, ratio, Qair_TEM, energy_saving_pc,
      smlAdequacy, cyanoSuppr, regime_ok, ote_full, ote_TEM, ote_ratio_pc,
    };
  }, [surfTempC, hypoTempC, meanDepth, smlDepth, Qair_full, chl_ugL, fCyano]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '-';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Waves} title="Module - Targeted Epilimnetic Mixing (TEM)"
        subtitle="Australian warm-water summer aeration regime - UWA Centre for Water Research lineage"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('tem_epilimnetic')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Modules
          </button>
        </div>}
      />
      <ModuleConflictBanner moduleId="tem_epilimnetic" />

      {/* KPI strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Q_air vs full mix</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.ratio * 100, 0)} %</div>
          <div className="text-xs text-slate-500 mt-1">= {fmt(R.Qair_TEM, 2)} L/s per diffuser</div>
        </div>
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Energy saving</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{fmt(R.energy_saving_pc, 0)} %</div>
          <div className="text-xs text-slate-500 mt-1">vs full destratification regime</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.cyanoSuppr > 70 ? 'border-emerald-700 bg-emerald-900/15' : R.cyanoSuppr > 40 ? 'border-amber-700 bg-amber-900/15' : 'border-rose-700 bg-rose-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-300">Cyano suppression index</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.cyanoSuppr, 0)} / 100</div>
          <div className="text-xs text-slate-500 mt-1">SML / z_crit = {fmt(R.smlAdequacy, 2)}</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${R.regime_ok ? 'border-cyan-700 bg-cyan-900/15' : 'border-slate-700 bg-slate-900'}`}>
          <div className="text-xs uppercase tracking-wider text-cyan-300">TEM regime active?</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{R.regime_ok ? 'YES' : 'no'}</div>
          <div className="text-xs text-slate-500 mt-1">{R.regime_ok ? 'warm + stratified + cyano-prone' : 'conditions outside TEM envelope'}</div>
        </div>
      </div>

      {/* Inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Slider label="Surface T"     suffix="C"    value={surfTempC} min={5}  max={32} step={0.5} onChange={setSurfTempC} />
        <Slider label="Hypo T"        suffix="C"    value={hypoTempC} min={2}  max={20} step={0.5} onChange={setHypoTempC} />
        <Slider label="Mean depth"    suffix="m"    value={meanDepth} min={1}  max={20} step={0.1} onChange={setMeanDepth} />
        <Slider label="SML depth"     suffix="m"    value={smlDepth}  min={0.3} max={meanDepth} step={0.1} onChange={setSmlDepth} />
        <Slider label="Wind"          suffix="m/s"  value={windMs}    min={0}  max={10} step={0.1} onChange={setWindMs} />
        <Slider label="Q_air (full)"  suffix="L/s"  value={Qair_full} min={0.5} max={10} step={0.1} onChange={setQairFull} />
        <Slider label="Diffusers"     value={diffusers} min={1} max={200} step={1} onChange={setDiffusers} />
        <Slider label="Chl-a"         suffix="ug/L" value={chl_ugL}   min={1}  max={150} step={1} onChange={setChl} />
        <Slider label="Cyano fraction" suffix="" value={fCyano}     min={0}  max={1} step={0.05} onChange={setFCyano} />
      </div>

      {/* Derived diagnostics */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <Stat label="Density difference (surf - hypo)"  value={`${fmt(R.dRho, 2)} kg/m3`} />
        <Stat label="Light attenuation k_d"             value={`${fmt(R.kd, 2)} 1/m`} />
        <Stat label="Euphotic depth (4.6 / k_d)"        value={`${fmt(R.z_eu, 2)} m`} />
        <Stat label="Critical depth z_crit (Hamilton)"  value={`${fmt(R.z_crit, 2)} m`} />
        <Stat label="SML / z_crit (target >= 1.0)"      value={fmt(R.smlAdequacy, 2)} ok={R.smlAdequacy >= 1.0} />
        <Stat label="Q_air ratio (TEM / full)"          value={`${fmt(R.ratio * 100, 1)} %`} />
        <Stat label="Total TEM airflow"                 value={`${fmt(R.Qair_TEM * diffusers, 1)} L/s`} />
        <Stat label="OTE re-cast (TEM / full)"          value={`${fmt(R.ote_ratio_pc, 0)} %`} />
        <Stat label="Per-device savings"                value={`${fmt((Qair_full - R.Qair_TEM) * 60 * 60 / 1000, 1)} m3/h per diffuser`} />
      </div>

      {/* Engineering rationale */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 space-y-3 text-sm text-slate-300">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-cyan-300">
          <Info className="w-4 h-4" /> Engineering basis & integration
        </h3>
        <p>
          TEM operates the same diffuser hardware at <strong>{fmt(R.ratio * 100, 0)} % of full-mix airflow</strong>, sized so
          the rising bubble plume terminates at the Surface Mixed Layer base (here {fmt(smlDepth, 1)} m) rather than the bed.
          The result: buoyant cyanobacteria (Microcystis, Anabaena) are entrained downward through the photic zone faster
          than their migration speed, preventing surface-scum formation, while the cold bottom water that the WTW abstracts
          is preserved.
        </p>
        <p>
          <strong>Why this fits a shallow polymictic reservoir like Barcombe</strong> — full destratification is over-engineered
          for a basin that re-mixes naturally every 5-10 days; the cyano risk peaks only in stable weather windows when
          surface heating intensifies. TEM is dialled-in to those windows, runs at {fmt(R.energy_saving_pc, 0)} % lower energy
          than full destratification, and is operationally complementary to the natural nocturnal-convection mixing already
          tracked by the MacIntyre module.
        </p>
        <p>
          <strong>Train integration</strong> - TEM acts as an alternate operating regime. When enabled, the McGinnis bubble-plume
          OTE is re-evaluated over the SML depth only (here OTE_TEM / OTE_full = {fmt(R.ote_ratio_pc, 0)} %); the Wuest detrainment
          height collapses to the SML base; the Kumagai cyano-stratification feedback loop is broken directly by surface
          entrainment. Deep-zone placement (where applicable) is retained for hardware, but operated at reduced Q_air during
          TEM windows.
        </p>
        <p>
          <strong>UK regulatory fit</strong> - Supports WFD Article 11 supplementary-measures requirement, DWI Regulation 4(2)(a)
          wholesomeness at source by suppressing taste-and-odour (geosmin / 2-MIB) precursor cyano populations, DEFRA
          cyanobacterial-risk guidance (mixing as primary intervention), and Ofwat AMP-8 resilience. No Reservoirs Act 1975
          Section 10 implications because no civil engineering is required - same hardware, different operating set-point.
        </p>
        <p className="text-xs text-slate-500 italic">
          Australian / Oceania research basis: Patterson, Hamblin & Imberger 1984 (UWA); Hamilton & Schladow 1997
          (UWA / now Griffith); Brookes & Carey 2011 (Adelaide, SA Water + Goyder Inst.); Antenucci, Brookes & Hipsey 2005
          (Centre for Water Research, UWA). Demonstrated sites: Chaffey Dam NSW, Cotter Dam ACT, Sugarloaf Reservoir Vic,
          Lake Burragorang NSW.
        </p>
      </div>

      {/* Verdict block */}
      <div className={`rounded-xl border-2 p-4 flex items-start gap-3 ${R.regime_ok && R.cyanoSuppr > 60 ? 'border-emerald-700 bg-emerald-900/15' : R.regime_ok ? 'border-amber-700 bg-amber-900/15' : 'border-slate-700 bg-slate-900'}`}>
        {R.regime_ok && R.cyanoSuppr > 60
          ? <ShieldCheck className="w-6 h-6 text-emerald-300 shrink-0 mt-0.5" />
          : <TrendingDown className="w-6 h-6 text-amber-300 shrink-0 mt-0.5" />}
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-100 mb-1">
            Verdict: {R.regime_ok && R.cyanoSuppr > 60
              ? `Run TEM regime - ${fmt(R.energy_saving_pc, 0)} % energy saving, ${fmt(R.cyanoSuppr, 0)} / 100 cyano suppression`
              : R.regime_ok
              ? `TEM viable but SML depth needs increasing (target SML >= ${fmt(R.z_crit, 1)} m for full suppression)`
              : 'Conditions outside TEM envelope - revert to full destratification or natural-mixing regime'}
          </div>
          <div className="text-xs text-slate-300">
            {R.regime_ok && R.cyanoSuppr > 60
              ? `Operate diffuser array at ${fmt(R.Qair_TEM, 2)} L/s per device (total ${fmt(R.Qair_TEM * diffusers, 1)} L/s) for the duration of the warm-weather window.`
              : R.regime_ok
              ? `Increase total Q_air slightly to deepen SML to ${fmt(R.z_crit, 1)} m, then re-evaluate.`
              : `Either surface T < 18 C, stratification < 4 C, or cyano fraction < 0.25 - TEM gain is marginal.`}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------- helpers ------------------------- */
function Slider(props: {
  label: string; value: number; min: number; max: number; step?: number; suffix?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-300">{props.label}</span>
        <span className="text-[10px] text-slate-500">{props.suffix} [{props.min}-{props.max}]</span>
      </div>
      <div className="flex items-center gap-2">
        <input type="range" min={props.min} max={props.max} step={props.step ?? 0.1} value={props.value}
          onChange={e => props.onChange(parseFloat(e.target.value) || 0)} className="flex-1 accent-cyan-500" />
        <input type="number" min={props.min} max={props.max} step={props.step ?? 0.1} value={props.value}
          onChange={e => props.onChange(parseFloat(e.target.value) || 0)}
          className="w-20 px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-xs" />
      </div>
    </label>
  );
}

function Stat({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${ok === undefined ? 'border-slate-700 bg-slate-800/50' : ok ? 'border-emerald-700 bg-emerald-900/15' : 'border-amber-700 bg-amber-900/15'}`}>
      <div className="text-slate-400 text-[10px] uppercase tracking-wide">{label}</div>
      <div className="text-slate-100 font-semibold mt-0.5">{value}</div>
    </div>
  );
}
